import type { ReactNode } from 'react';
import { ScannedAt } from './ScannedAt';

/**
 * The detail-page title row — contract point 1 of the redesign.
 *
 * Entity name in mono (it is an identifier you copy, not prose), ONE status
 * badge, and the facts that identify it as a single muted line. The pages used to
 * open with a `.kv-grid` of cells: six bordered boxes for six short values, which
 * gave a group path and a visibility flag the same visual weight as the entity's
 * own name and pushed the actual content below the fold.
 *
 * `facts` are joined with · here rather than by each caller, so no page invents
 * its own separator.
 */
export function DetailHead({
  name,
  badge,
  facts,
  scannedAt,
  curated,
  scanLead,
  scanNote,
  actions,
}: {
  name: string;
  badge?: ReactNode;
  /** Short identifying values. Falsy entries are dropped, so callers can pass
   *  optional fields inline without guarding each one. */
  facts?: (ReactNode | null | undefined | false)[];
  scannedAt?: string | null;
  curated?: boolean;
  /** Passed through to ScannedAt — why this entity has no scan time. */
  scanLead?: string;
  scanNote?: string;
  actions?: ReactNode;
}) {
  const shown = (facts ?? []).filter(Boolean);
  return (
    <div className="detail-title">
      <div className="detail-title__row">
        <h2 className="detail-title__name">{name}</h2>
        {badge}
        <div className="spacer" />
        {actions}
      </div>
      {shown.length > 0 && (
        <p className="detail-title__facts">
          {shown.map((f, i) => (
            <span key={i}>
              {i > 0 && <span className="detail-title__sep"> · </span>}
              {f}
            </span>
          ))}
        </p>
      )}
      <ScannedAt at={scannedAt} curated={curated} lead={scanLead} note={scanNote} />
    </div>
  );
}
